import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { vars, Button, Input, gtagEvent } from '@agendor/ui';
import { NotesTextArea, ButtonGroup, ErrorText, FormGroup, InputGroup } from '../shared-styles';
import BookingService from '../booking-service';
import { BookingApi } from '@agendor/shared';

const SuccessIcon = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: ${vars.successTintSoft};
    border: 1px solid ${vars.dividerColor};
    color: ${vars.accentColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 20px;
`;

const RequiredMark = styled.span`
    color: ${vars.dangerColor};
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 16px;
`;

const RadioColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
`;

const StepperLabel = styled.div`
    font-size: 0.8rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

type Props = {
    bookingService: BookingService;
    businessSlug: string;
    bookingId: string;
    /** `null` to fetch from the server; non-null uses the provided list directly. */
    intakes: BookingApi.BookingIntake[] | null;
    onViewBookings: () => void;
    onComplete: (() => void) | null;
    onSkip: (() => void) | null;
};

type AnswersByIntake = Record<number, Record<number, string>>;

export default function IntakeSection({
    bookingService,
    businessSlug,
    bookingId,
    intakes: initialIntakes,
    onViewBookings,
    onComplete,
    onSkip,
}: Props) {
    const [status, setStatus] = useState<'loading' | 'form' | 'completed' | 'error'>(
        initialIntakes ? (initialIntakes.length === 0 ? 'error' : 'form') : 'loading',
    );
    const [intakes, setIntakes] = useState<BookingApi.BookingIntake[]>(initialIntakes ?? []);
    const [answers, setAnswers] = useState<AnswersByIntake>({});
    const [stepIndex, setStepIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const loadIntake = useCallback(async () => {
        try {
            const fetched = await bookingService.getIntake(businessSlug, bookingId);
            const pending = fetched.filter((i) => i.status === 'pending');
            if (pending.length === 0) {
                if (fetched.length > 0 && fetched.every((i) => i.status === 'completed')) {
                    setStatus('completed');
                } else {
                    setStatus('error');
                }
                return;
            }
            setIntakes(pending);
            setStatus('form');
        } catch {
            setStatus('error');
        }
    }, [bookingService, businessSlug, bookingId]);

    useEffect(() => {
        if (!initialIntakes) loadIntake();
    }, [initialIntakes, loadIntake]);

    const total = intakes.length;
    const isMulti = total > 1;
    const currentIntake = intakes[stepIndex];
    const isLast = stepIndex === total - 1;

    const requiredFilledForCurrent = (() => {
        if (!currentIntake) return true;
        const intakeAnswers = answers[currentIntake.id] ?? {};
        return currentIntake.questions.every((q) => {
            if (!q.required) return true;
            const a = intakeAnswers[q.id];
            return a !== undefined && a.trim().length > 0;
        });
    })();

    const setAnswer = (intakeId: number, questionId: number, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [intakeId]: { ...(prev[intakeId] ?? {}), [questionId]: value },
        }));
    };

    const handleNext = async () => {
        if (!isLast) {
            gtagEvent('intake_next_click', { value: stepIndex });
            setStepIndex((i) => i + 1);
            return;
        }
        gtagEvent('intake_submit_click', { value: total });
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const submissions: BookingApi.IntakeSubmission[] = intakes.map((intake) => ({
                intakeId: intake.id,
                answers: Object.entries(answers[intake.id] ?? {}).map(([qid, text]) => ({
                    questionId: Number(qid),
                    answerText: text,
                })),
            }));
            await bookingService.submitIntake(businessSlug, bookingId, submissions);
            if (onComplete) {
                onComplete();
                return;
            }
            setStatus('completed');
        } catch {
            setSubmitError('Não foi possível enviar as respostas. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrev = () => {
        gtagEvent('intake_back_click', { value: stepIndex });
        if (stepIndex > 0) {
            setStepIndex((i) => i - 1);
        } else if (onSkip) {
            onSkip();
        } else {
            onViewBookings();
        }
    };

    if (status === 'loading') {
        return <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>Carregando...</div>;
    }

    if (status === 'error') {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p>Formulário não disponível.</p>
            </div>
        );
    }

    if (status === 'completed') {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <SuccessIcon>✓</SuccessIcon>
                <h2 style={{ marginBottom: 8 }} data-testid="intake-success">
                    Respostas salvas!
                </h2>
                <p>Obrigado por preencher!</p>
                <ButtonGroup style={{ justifyContent: 'center', marginTop: 24 }}>
                    <Button primary onClick={onViewBookings}>
                        Ver meus agendamentos
                    </Button>
                </ButtonGroup>
            </div>
        );
    }

    if (!currentIntake) return null;

    const intakeAnswers = answers[currentIntake.id] ?? {};

    return (
        <>
            {isMulti && (
                <StepperLabel data-testid="intake-stepper">
                    Formulário {stepIndex + 1} de {total}
                </StepperLabel>
            )}
            <h2 data-testid="intake-form-title">{isMulti ? currentIntake.name : 'Informações adicionais'}</h2>
            <p>Preencha as informações abaixo antes da sua consulta.</p>
            <FormGroup style={{ marginTop: 16 }}>
                {currentIntake.questions.map((question) => (
                    <InputGroup key={question.id} as="div">
                        <label style={{ fontWeight: 600, fontSize: '0.9rem', color: vars.foregroundColor }}>
                            {question.questionText}
                            {question.required && <RequiredMark> *</RequiredMark>}
                        </label>
                        {question.type === 'text' && (
                            <Input
                                value={intakeAnswers[question.id] ?? ''}
                                onChange={(e) => setAnswer(currentIntake.id, question.id, e.target.value)}
                                data-testid={`intake-answer-${question.id}`}
                            />
                        )}
                        {question.type === 'textarea' && (
                            <NotesTextArea
                                value={intakeAnswers[question.id] ?? ''}
                                onChange={(e) => setAnswer(currentIntake.id, question.id, e.target.value)}
                                data-testid={`intake-answer-${question.id}`}
                            />
                        )}
                        {question.type === 'boolean' && (
                            <RadioGroup>
                                {['true', 'false'].map((val) => (
                                    <RadioLabel key={val}>
                                        <input
                                            type="radio"
                                            name={`intake-${currentIntake.id}-${question.id}`}
                                            checked={intakeAnswers[question.id] === val}
                                            onChange={() => setAnswer(currentIntake.id, question.id, val)}
                                            data-testid={`intake-answer-${question.id}-${val}`}
                                        />
                                        {val === 'true' ? 'Sim' : 'Não'}
                                    </RadioLabel>
                                ))}
                            </RadioGroup>
                        )}
                        {question.type === 'enum' && question.options && (
                            <RadioColumn>
                                {question.options.map((option) => (
                                    <RadioLabel key={option}>
                                        <input
                                            type="radio"
                                            name={`intake-${currentIntake.id}-${question.id}`}
                                            checked={intakeAnswers[question.id] === option}
                                            onChange={() => setAnswer(currentIntake.id, question.id, option)}
                                            data-testid={`intake-answer-${question.id}-${option}`}
                                        />
                                        {option}
                                    </RadioLabel>
                                ))}
                            </RadioColumn>
                        )}
                    </InputGroup>
                ))}
            </FormGroup>
            {submitError && <ErrorText style={{ marginTop: 12 }}>{submitError}</ErrorText>}
            <ButtonGroup style={{ marginTop: 24 }}>
                {stepIndex === 0 && onSkip ? (
                    <Button
                        onClick={() => {
                            gtagEvent('intake_skip_click', { value: total });
                            onSkip();
                        }}
                        data-testid="intake-skip-btn"
                    >
                        Pular
                    </Button>
                ) : (
                    <Button onClick={handlePrev} data-testid="intake-back-btn">
                        Voltar
                    </Button>
                )}
                <Button
                    primary
                    onClick={handleNext}
                    disabled={!requiredFilledForCurrent || isSubmitting}
                    data-testid={isLast ? 'intake-submit-btn' : 'intake-next-btn'}
                >
                    {isSubmitting ? 'Enviando...' : isLast ? 'Enviar' : 'Próxima'}
                </Button>
            </ButtonGroup>
        </>
    );
}
